import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

export interface GridListAction {
  title: string;
  href: string;
  icon: LucideIcon;
  iconForeground: string;
  iconBackground: string;
  description?: string;
}

interface GridListProps {
  actions: GridListAction[];
  className?: string;
}

export function GridList({ actions, className }: GridListProps) {
  return (
    <div
      className={cn(
        'divide-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0',
        className,
      )}
    >
      {actions.map((action, actionIdx) => {
        const Icon = action.icon;
        return (
          <div
            key={action.title}
            className={cn(
              actionIdx === 0 ? 'rounded-tl-lg rounded-tr-lg sm:rounded-tr-none' : '',
              actionIdx === 1 ? 'sm:rounded-tr-lg' : '',
              actionIdx === actions.length - 2 ? 'sm:rounded-bl-lg' : '',
              actionIdx === actions.length - 1
                ? 'rounded-bl-lg rounded-br-lg sm:rounded-bl-none'
                : '',
              'group relative bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500',
              'flex gap-6',
              'transition-all duration-200 ease-in-out',
              'hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:-translate-y-1',
              'cursor-pointer',
            )}
          >
            <div>
              <span
                className={cn(
                  action.iconBackground,
                  action.iconForeground,
                  'inline-flex rounded-lg p-3 ring-4 ring-white',
                  'transition-all duration-200 ease-in-out',
                  'group-hover:scale-110 group-hover:shadow-md',
                )}
              >
                <Icon
                  aria-hidden="true"
                  className="h-6 w-6 transition-transform duration-200 group-hover:rotate-6"
                />
              </span>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 transition-colors duration-200 group-hover:text-blue-600">
                <Link to={action.href} className="focus:outline-none">
                  <span aria-hidden="true" className="absolute inset-0" />
                  {action.title}
                </Link>
              </h3>
              {action.description && (
                <p className="mt-2 text-sm text-gray-500 transition-colors duration-200 group-hover:text-gray-700">
                  {action.description}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
